import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  AlertTriangle,
  Calendar,
  User,
  FileText,
  MapPin,
  Hash,
  DollarSign,
  Save,
  RefreshCw,
  Lock,
  Info,
  Building,
  Search,
  Filter,
  CheckSquare,
  Square,
  LayoutList,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  Zap,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { useStockCosting } from "@/hooks/use-stock-costing";
import { CostImpactWarning } from "@/components/cost-impact-warning";
import { mockWarehouses, mockLocations } from "@/data/mockWarehousesLocations";
import { updateLocationStock, getWarehouseLocations } from "@/services/location-stock-service";

// Types
interface PurchaseOrderLine {
  id: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  description: string;
  quantityOrdered: number;
  quantityReceived: number;
  quantityOutstanding: number;
  unitPrice: number;
  lineTotal: number;
}

interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierCode: string;
  supplierName: string;
  orderDate: Date;
  expectedDeliveryDate: Date;
  status: string;
  totalValue: number;
  currency: string;
  lines: PurchaseOrderLine[];
}

interface ReceivingLine {
  poLineId: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  description: string;
  quantityOrdered: number;
  quantityPreviouslyReceived: number;
  quantityOutstanding: number;
  quantityToReceive: number;
  unitPrice: number;
  warehouseId: string;
  locationId: string;
  lotBatchSerial: string;
  isSelected: boolean;
  isFullyReceived: boolean;
}

interface Warehouse {
  id: string;
  code: string;
  name: string;
}

interface Location {
  id: string;
  warehouseId: string;
  code: string;
  name: string;
}

// Mock data - Purchase Orders
const MOCK_PURCHASE_ORDERS: PurchaseOrder[] = [
  {
    id: "1",
    poNumber: "PO-2024-001",
    supplierCode: "SUP-001",
    supplierName: "Supplier A Ltd",
    orderDate: new Date("2024-01-15"),
    expectedDeliveryDate: new Date("2024-02-15"),
    status: "partially-received",
    totalValue: 15750.00,
    currency: "GBP",
    lines: [
      {
        id: "l1",
        lineNumber: 1,
        stockItemCode: "WGT-A",
        stockItemName: "Widget A",
        description: "Premium quality widget - Model A",
        quantityOrdered: 150,
        quantityReceived: 100,
        quantityOutstanding: 50,
        unitPrice: 45.00,
        lineTotal: 6750.00
      },
      {
        id: "l2",
        lineNumber: 2,
        stockItemCode: "PART-C",
        stockItemName: "Component C",
        description: "Standard component C - Pack of 10",
        quantityOrdered: 200,
        quantityReceived: 200,
        quantityOutstanding: 0,
        unitPrice: 22.50,
        lineTotal: 4500.00
      },
      {
        id: "l3",
        lineNumber: 3,
        stockItemCode: "RAW-X",
        stockItemName: "Raw Material X",
        description: "Raw material X - 25kg bags",
        quantityOrdered: 100,
        quantityReceived: 0,
        quantityOutstanding: 100,
        unitPrice: 45.00,
        lineTotal: 4500.00
      }
    ]
  },
  {
    id: "2",
    poNumber: "PO-2024-002",
    supplierCode: "SUP-002",
    supplierName: "Supplier B Corp",
    orderDate: new Date("2024-01-20"),
    expectedDeliveryDate: new Date("2024-01-25"),
    status: "overdue",
    totalValue: 8500.00,
    currency: "GBP",
    lines: [
      {
        id: "l4",
        lineNumber: 1,
        stockItemCode: "WGT-B",
        stockItemName: "Widget B",
        description: "Widget B - Standard model",
        quantityOrdered: 100,
        quantityReceived: 0,
        quantityOutstanding: 100,
        unitPrice: 85.00,
        lineTotal: 8500.00
      }
    ]
  },
  {
    id: "3",
    poNumber: "PO-2024-003",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Co",
    orderDate: new Date("2024-01-22"),
    expectedDeliveryDate: new Date("2024-02-28"),
    status: "open",
    totalValue: 25600.00,
    currency: "GBP",
    lines: [
      {
        id: "l5",
        lineNumber: 1,
        stockItemCode: "GDG-B",
        stockItemName: "Gadget B",
        description: "Gadget B - Professional series",
        quantityOrdered: 200,
        quantityReceived: 0,
        quantityOutstanding: 200,
        unitPrice: 128.00,
        lineTotal: 25600.00
      }
    ]
  }
];

// Mock data - Warehouses
const MOCK_WAREHOUSES: Warehouse[] = [
  { id: "wh1", code: "MAIN", name: "Main Warehouse" },
  { id: "wh2", code: "OVERFLOW", name: "Overflow Warehouse" },
  { id: "wh3", code: "QC", name: "Quality Control Hold" }
];

// Mock data - Locations
const MOCK_LOCATIONS: Location[] = [
  { id: "loc1", warehouseId: "wh1", code: "A1", name: "Aisle A, Bin 1" },
  { id: "loc2", warehouseId: "wh1", code: "A2", name: "Aisle A, Bin 2" },
  { id: "loc3", warehouseId: "wh1", code: "B1", name: "Aisle B, Bin 1" },
  { id: "loc4", warehouseId: "wh1", code: "C3", name: "Aisle C, Bin 3" },
  { id: "loc5", warehouseId: "wh2", code: "Z1", name: "Zone Z, Bin 1" },
  { id: "loc6", warehouseId: "wh3", code: "QC-1", name: "QC Hold Area 1" }
];

interface ReceiveGoodsPageProps {
  pageParams?: {
    poId?: string;
    poNumber?: string;
    returnTo?: string;
  };
}

export function ReceiveGoodsPage({ pageParams }: ReceiveGoodsPageProps) {
  const { navigateTo } = useNavigation();
  const { processPurchaseReceipt, valuationMethod, getValuationMethodDisplayName } = useStockCosting();
  
  // Determine if pre-loaded from PO page
  const isPreloaded = !!(pageParams?.poId || pageParams?.poNumber);
  const returnTo = pageParams?.returnTo;

  // State
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [deliveryDate, setDeliveryDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [grnReference, setGrnReference] = useState<string>("");
  const [receivedBy, setReceivedBy] = useState<string>("John Doe");
  const [notes, setNotes] = useState<string>("");
  const [receivingLines, setReceivingLines] = useState<ReceivingLine[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // New state for UX improvements
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showOutstandingOnly, setShowOutstandingOnly] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<"detailed" | "table">("detailed");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(50);
  const [defaultWarehouseId, setDefaultWarehouseId] = useState<string>("");
  const [defaultLocationId, setDefaultLocationId] = useState<string>("");
  
  // Collapsible sections state
  const [isPOSectionCollapsed, setIsPOSectionCollapsed] = useState<boolean>(false);
  const [isReceivingDetailsCollapsed, setIsReceivingDetailsCollapsed] = useState<boolean>(true);
  const [isQuickSetupCollapsed, setIsQuickSetupCollapsed] = useState<boolean>(true);
  const [isCostImpactCollapsed, setIsCostImpactCollapsed] = useState<boolean>(true);

  // Initialize page
  useEffect(() => {
    if (isPreloaded) {
      // Load specific PO
      const poId = pageParams?.poId;
      const po = MOCK_PURCHASE_ORDERS.find(p => p.id === poId);
      
      if (po) {
        setSelectedPO(po);
        setReceivingLines(createReceivingLines(po));
        setGrnReference(generateGRNReference());
      } else {
        toast.error("Purchase order not found");
      }
    } else {
      // Generate GRN reference for new receiving
      setGrnReference(generateGRNReference());
    }
  }, [isPreloaded, pageParams]);

  // Get available POs for dropdown (only open/partially-received)
  const availablePOs = useMemo(() => {
    return MOCK_PURCHASE_ORDERS.filter(po => 
      po.status === "open" || po.status === "partially-received" || po.status === "overdue"
    );
  }, []);

  // Create receiving lines from PO
  const createReceivingLines = (po: PurchaseOrder): ReceivingLine[] => {
    return po.lines.map(line => ({
      poLineId: line.id,
      lineNumber: line.lineNumber,
      stockItemCode: line.stockItemCode,
      stockItemName: line.stockItemName,
      description: line.description,
      quantityOrdered: line.quantityOrdered,
      quantityPreviouslyReceived: line.quantityReceived,
      quantityOutstanding: line.quantityOutstanding,
      quantityToReceive: line.quantityOutstanding, // Pre-fill with outstanding
      unitPrice: line.unitPrice,
      warehouseId: mockWarehouses[0]?.code || "", // Default to first warehouse
      locationId: "",
      lotBatchSerial: "",
      isSelected: line.quantityOutstanding > 0, // Auto-select items with outstanding qty
      isFullyReceived: line.quantityOutstanding === 0
    }));
  };

  // Generate GRN reference
  const generateGRNReference = (): string => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 9000) + 1000;
    return `GRN-${year}-${random}`;
  };

  // Handle PO selection (only for non-preloaded mode)
  const handlePOSelection = (poId: string) => {
    if (isPreloaded) {
      toast.error("Cannot change PO when receiving from Purchase Orders page");
      return;
    }

    const po = MOCK_PURCHASE_ORDERS.find(p => p.id === poId);
    if (po) {
      setSelectedPO(po);
      setReceivingLines(createReceivingLines(po));
    }
  };

  // Handle line selection toggle
  const handleLineToggle = (lineId: string) => {
    setReceivingLines(lines =>
      lines.map(line =>
        line.poLineId === lineId
          ? { ...line, isSelected: !line.isSelected }
          : line
      )
    );
  };

  // Handle quantity change
  const handleQuantityChange = (lineId: string, value: string) => {
    const quantity = parseInt(value) || 0;
    setReceivingLines(lines =>
      lines.map(line =>
        line.poLineId === lineId
          ? { ...line, quantityToReceive: quantity }
          : line
      )
    );
  };

  // Handle warehouse change
  const handleWarehouseChange = (lineId: string, warehouseId: string) => {
    setReceivingLines(lines =>
      lines.map(line =>
        line.poLineId === lineId
          ? { ...line, warehouseId, locationId: "" } // Reset location when warehouse changes
          : line
      )
    );
  };

  // Handle location change
  const handleLocationChange = (lineId: string, locationId: string) => {
    setReceivingLines(lines =>
      lines.map(line =>
        line.poLineId === lineId
          ? { ...line, locationId }
          : line
      )
    );
  };

  // Handle lot/batch change
  const handleLotBatchChange = (lineId: string, value: string) => {
    setReceivingLines(lines =>
      lines.map(line =>
        line.poLineId === lineId
          ? { ...line, lotBatchSerial: value }
          : line
      )
    );
  };

  // Get locations for warehouse
  const getLocationsForWarehouse = (warehouseId: string): Location[] => {
    const locations = getWarehouseLocations(warehouseId);
    return locations.map(loc => ({
      id: loc.locationCode,
      warehouseId: loc.warehouseCode,
      code: loc.locationCode,
      name: loc.locationCode
    }));
  };

  // Filter and search lines
  const filteredLines = useMemo(() => {
    let filtered = receivingLines;

    // Filter by outstanding only
    if (showOutstandingOnly) {
      filtered = filtered.filter(l => !l.isFullyReceived);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        l =>
          l.stockItemCode.toLowerCase().includes(query) ||
          l.stockItemName.toLowerCase().includes(query) ||
          l.description.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [receivingLines, showOutstandingOnly, searchQuery]);

  // Paginated lines
  const paginatedLines = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredLines.slice(startIndex, endIndex);
  }, [filteredLines, currentPage, itemsPerPage]);

  // Total pages
  const totalPages = Math.ceil(filteredLines.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showOutstandingOnly]);

  // Bulk action handlers (defined after pagination logic)
  const handleSelectAllVisible = () => {
    const visibleIds = new Set(paginatedLines.map(l => l.poLineId));
    setReceivingLines(lines =>
      lines.map(line =>
        visibleIds.has(line.poLineId) && !line.isFullyReceived
          ? { ...line, isSelected: true }
          : line
      )
    );
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = new Set(filteredLines.map(l => l.poLineId));
    setReceivingLines(lines =>
      lines.map(line =>
        filteredIds.has(line.poLineId) && !line.isFullyReceived
          ? { ...line, isSelected: true }
          : line
      )
    );
  };

  const handleDeselectAll = () => {
    setReceivingLines(lines =>
      lines.map(line => ({ ...line, isSelected: false }))
    );
  };

  const handleFillQuantitiesToMax = () => {
    setReceivingLines(lines =>
      lines.map(line =>
        line.isSelected && !line.isFullyReceived
          ? { ...line, quantityToReceive: line.quantityOutstanding }
          : line
      )
    );
    toast.success("Quantities filled to maximum outstanding");
  };

  const handleApplyDefaults = () => {
    if (!defaultWarehouseId) {
      toast.error("Please select a default warehouse first");
      return;
    }

    setReceivingLines(lines =>
      lines.map(line =>
        line.isSelected && !line.isFullyReceived
          ? {
              ...line,
              warehouseId: defaultWarehouseId,
              locationId: defaultLocationId || line.locationId
            }
          : line
      )
    );
    
    const message = defaultLocationId 
      ? "Warehouse and location applied to selected items"
      : "Warehouse applied to selected items";
    toast.success(message);
  };

  // Calculate summary
  const summary = useMemo(() => {
    const selectedLines = receivingLines.filter(l => l.isSelected && !l.isFullyReceived);
    const itemCount = selectedLines.length;
    const totalUnits = selectedLines.reduce((sum, line) => sum + line.quantityToReceive, 0);
    const totalValue = selectedLines.reduce(
      (sum, line) => sum + (line.quantityToReceive * line.unitPrice),
      0
    );

    return { itemCount, totalUnits, totalValue };
  }, [receivingLines]);

  // Validation
  const validate = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!selectedPO) {
      errors.push("Please select a purchase order");
      return { isValid: false, errors };
    }

    const selectedLines = receivingLines.filter(l => l.isSelected && !l.isFullyReceived);

    if (selectedLines.length === 0) {
      errors.push("Please select at least one item to receive");
    }

    selectedLines.forEach(line => {
      if (line.quantityToReceive <= 0) {
        errors.push(`${line.stockItemName}: Quantity must be greater than 0`);
      }

      if (line.quantityToReceive > line.quantityOutstanding) {
        errors.push(
          `${line.stockItemName}: Cannot receive more than outstanding (${line.quantityOutstanding})`
        );
      }

      if (!line.warehouseId) {
        errors.push(`${line.stockItemName}: Please select a warehouse`);
      }

      if (!line.locationId) {
        errors.push(`${line.stockItemName}: Please select a location`);
      }
    });

    if (!grnReference.trim()) {
      errors.push("Please enter a GRN reference");
    }

    if (!deliveryDate) {
      errors.push("Please enter a delivery date");
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Handle confirm receipt
  const handleConfirmReceipt = () => {
    const validation = validate();

    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    setShowConfirmDialog(true);
  };

  // Handle submit
  const handleSubmit = async () => {
    setIsSaving(true);
    setShowConfirmDialog(false);

    try {
      // Process costing calculations for each received line
      const linesToReceive = receivingLines.filter(l => l.isSelected && !l.isFullyReceived);
      
      console.log(`📦 Processing goods receipt using ${getValuationMethodDisplayName()}`);
      
      for (const line of linesToReceive) {
        // In a real app, you'd fetch the current stock item data from the API
        // For now, we'll simulate it
        const mockStockItem = {
          code: line.stockItemCode,
          physicalQuantity: 100, // Mock current quantity
          averageCost: line.unitPrice * 0.9, // Mock current average cost
        };

        // Get warehouse code for FIFO tracking
        const warehouse = mockWarehouses.find(w => w.code === line.warehouseId);
        const warehouseCode = warehouse?.code || 'DEFAULT';
        
        // Get location code for FIFO tracking
        const location = mockLocations.find(l => l.locationCode === line.locationId);
        const locationCode = location?.locationCode;

        // Process receipt - automatically uses FIFO or Average Cost based on settings
        const updates = processPurchaseReceipt(
          mockStockItem as any,
          line.quantityToReceive,
          line.unitPrice,
          warehouseCode,
          locationCode,
          grnReference
        );

        if (valuationMethod === 'fifo') {
          console.log(`✅ FIFO Receipt: ${line.stockItemCode} - Qty: ${line.quantityToReceive}, Unit Cost: £${line.unitPrice.toFixed(2)}, Warehouse: ${warehouseCode}, Location: ${locationCode || 'N/A'}, New Avg: £${updates.averageCost?.toFixed(4)}`);
        } else {
          console.log(`✅ Average Cost Receipt: ${line.stockItemCode} - Qty: ${line.quantityToReceive}, Unit Cost: £${line.unitPrice.toFixed(2)}, New Avg: £${updates.averageCost?.toFixed(4)}, New Qty: ${updates.physicalQuantity}`);
        }
        
        // Update location stock levels
        if (locationCode) {
          const stockUpdateSuccess = updateLocationStock(locationCode, line.quantityToReceive);
          if (stockUpdateSuccess) {
            console.log(`✅ Location stock updated: ${locationCode} (+${line.quantityToReceive})`);
          }
        }
        
        // In real app: await api.updateStockItem(line.stockItemCode, updates);
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // In real app: Submit to backend
      const receivingData = {
        poId: selectedPO!.id,
        poNumber: selectedPO!.poNumber,
        deliveryDate,
        grnReference,
        receivedBy,
        notes,
        lines: linesToReceive.map(l => ({
          poLineId: l.poLineId,
          stockItemCode: l.stockItemCode,
          quantityReceived: l.quantityToReceive,
          unitCost: l.unitPrice,
          warehouseId: l.warehouseId,
          locationId: l.locationId,
          lotBatchSerial: l.lotBatchSerial || null
        }))
      };

      console.log("Submitting receiving data:", receivingData);

      toast.success(`Goods received successfully! GRN: ${grnReference}`);

      // Navigate back or reset
      if (returnTo === "purchase-orders") {
        setTimeout(() => {
          navigateTo("purchase-orders");
        }, 1000);
      } else {
        // Reset form for next receiving
        setSelectedPO(null);
        setReceivingLines([]);
        setNotes("");
        setGrnReference(generateGRNReference());
        toast.info("Ready to receive from another PO");
      }
    } catch (error) {
      toast.error("Failed to record receipt. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save draft
  const handleSaveDraft = () => {
    toast.promise(
      new Promise(resolve => setTimeout(resolve, 1000)),
      {
        loading: "Saving draft...",
        success: "Draft saved successfully",
        error: "Failed to save draft"
      }
    );
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = "GBP") => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Check if receiving details are complete
  const isReceivingDetailsComplete = useMemo(() => {
    return !!(deliveryDate && grnReference.trim() && receivedBy);
  }, [deliveryDate, grnReference, receivedBy]);

  // Check if quick setup has values
  const hasQuickSetupValues = useMemo(() => {
    return !!(defaultWarehouseId || defaultLocationId);
  }, [defaultWarehouseId, defaultLocationId]);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Page Header - Fixed */}
      <div className="flex-shrink-0 pb-4 border-b bg-background">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Receive Goods</h1>
              {isPreloaded && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  <Lock className="h-3 w-3 mr-1" />
                  Pre-loaded
                </Badge>
              )}
              <Badge 
                variant="outline" 
                className={valuationMethod === 'fifo' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-green-50 text-green-700 border-green-200'}
              >
                <Info className="h-3 w-3 mr-1" />
                {getValuationMethodDisplayName()}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-2">
              Record goods received against purchase orders • Cost layers will be tracked using {getValuationMethodDisplayName()}
            </p>
          </div>
          <div className="flex gap-2">
            {returnTo === "purchase-orders" && (
              <Button
                variant="outline"
                onClick={() => navigateTo("purchase-orders")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Purchase Orders
              </Button>
            )}
            <Button variant="outline" onClick={() => navigateTo("purchase-orders")}>
              <Package className="h-4 w-4 mr-2" />
              View All POs
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto py-6 space-y-6">
        {/* Valuation Method Info Alert */}
        {valuationMethod === 'fifo' && (
          <Alert className="border-purple-200 bg-purple-50">
            <Info className="h-4 w-4 text-purple-600" />
            <AlertTitle className="text-purple-900">FIFO Costing Active</AlertTitle>
            <AlertDescription className="text-purple-700">
              Cost layers will be created for each goods receipt. When selling, the system will consume from the oldest cost layers first (FIFO method).
              This provides more accurate inventory valuation at current costs.
            </AlertDescription>
          </Alert>
        )}

        {/* PO Selection / Details */}
        <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setIsPOSectionCollapsed(!isPOSectionCollapsed)}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                {isPreloaded ? (
                  <>
                    <Lock className="h-5 w-5 text-blue-600" />
                    Purchase Order Details
                  </>
                ) : (
                  <>
                    <Package className="h-5 w-5" />
                    Select Purchase Order
                  </>
                )}
                {selectedPO && isPOSectionCollapsed && (
                  <Badge variant="outline" className="ml-2 font-mono">
                    {selectedPO.poNumber}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {isPreloaded
                  ? "This purchase order is pre-selected and cannot be changed"
                  : "Choose a purchase order to receive goods against"}
              </CardDescription>
            </div>
            <div className="ml-2">
              {isPOSectionCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </div>
          </div>
        </CardHeader>
        {!isPOSectionCollapsed && (
          <CardContent>
            {isPreloaded ? (
              // Pre-loaded PO details
              selectedPO && (
              <div className="bg-muted/50 p-4 rounded-lg space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">PO Number</Label>
                    <p className="font-mono font-bold text-lg">{selectedPO.poNumber}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Supplier</Label>
                    <p className="font-medium">{selectedPO.supplierName}</p>
                    <p className="text-sm text-muted-foreground">{selectedPO.supplierCode}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Expected Delivery</Label>
                    <p className="font-medium font-mono">{formatDate(selectedPO.expectedDeliveryDate)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Outstanding Value</Label>
                    <p className="font-bold text-lg text-blue-600">
                      {formatCurrency(
                        selectedPO.lines.reduce((sum, line) => 
                          sum + (line.quantityOutstanding * line.unitPrice), 0
                        ),
                        selectedPO.currency
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )
          ) : (
            // PO Selector
            <div className="space-y-4">
              <div>
                <Label>Purchase Order</Label>
                <Select
                  value={selectedPO?.id || ""}
                  onValueChange={handlePOSelection}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a purchase order..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePOs.map(po => {
                      const outstandingValue = po.lines.reduce(
                        (sum, line) => sum + (line.quantityOutstanding * line.unitPrice),
                        0
                      );
                      return (
                        <SelectItem key={po.id} value={po.id}>
                          <div className="flex items-center justify-between w-full">
                            <span className="font-mono font-medium">{po.poNumber}</span>
                            <span className="text-muted-foreground ml-4">{po.supplierName}</span>
                            <span className="text-blue-600 font-medium ml-4">
                              {formatCurrency(outstandingValue, po.currency)} outstanding
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {selectedPO && (
                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">Supplier</Label>
                      <p className="font-medium">{selectedPO.supplierName}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Order Date</Label>
                      <p className="font-mono">{formatDate(selectedPO.orderDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Expected Delivery</Label>
                      <p className="font-mono">{formatDate(selectedPO.expectedDeliveryDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Total Value</Label>
                      <p className="font-medium">{formatCurrency(selectedPO.totalValue, selectedPO.currency)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          </CardContent>
        )}
        </Card>

        {/* Receiving Form */}
        {selectedPO && receivingLines.length > 0 && (
          <>
            {/* Receiving Details */}
            <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => setIsReceivingDetailsCollapsed(!isReceivingDetailsCollapsed)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Receiving Details
                    {isReceivingDetailsCollapsed && (
                      <Badge 
                        variant={isReceivingDetailsComplete ? "success" : "outline"}
                        accent={isReceivingDetailsComplete ? "success" : undefined}
                        className="ml-2"
                      >
                        {isReceivingDetailsComplete ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Complete
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Required
                          </>
                        )}
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Enter delivery information and GRN reference
                  </CardDescription>
                </div>
                <div className="ml-2">
                  {isReceivingDetailsCollapsed ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </div>
              </div>
            </CardHeader>
            {!isReceivingDetailsCollapsed && (
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="deliveryDate">
                    <Calendar className="h-4 w-4 inline mr-2" />
                    Delivery Date *
                  </Label>
                  <Input
                    id="deliveryDate"
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="grnReference">
                    <Hash className="h-4 w-4 inline mr-2" />
                    GRN Reference *
                  </Label>
                  <Input
                    id="grnReference"
                    value={grnReference}
                    onChange={(e) => setGrnReference(e.target.value)}
                    placeholder="GRN-2024-0001"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="receivedBy">
                    <User className="h-4 w-4 inline mr-2" />
                    Received By *
                  </Label>
                  <Select value={receivedBy} onValueChange={setReceivedBy}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="John Doe">John Doe</SelectItem>
                      <SelectItem value="Jane Smith">Jane Smith</SelectItem>
                      <SelectItem value="Mike Wilson">Mike Wilson</SelectItem>
                      <SelectItem value="Sarah Johnson">Sarah Johnson</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="notes">
                    <FileText className="h-4 w-4 inline mr-2" />
                    Notes (Optional)
                  </Label>
                  <Input
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>
              </CardContent>
            )}
            </Card>

            {/* Default Warehouse/Location (Optional) */}
            <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => setIsQuickSetupCollapsed(!isQuickSetupCollapsed)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-orange-600" />
                    Quick Setup (Optional)
                    {isQuickSetupCollapsed && hasQuickSetupValues && (
                      <Badge variant="outline" className="ml-2">
                        <Info className="h-3 w-3 mr-1" />
                        Configured
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Set default warehouse and location to apply to all selected items
                  </CardDescription>
                </div>
                <div className="ml-2">
                  {isQuickSetupCollapsed ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </div>
              </div>
            </CardHeader>
            {!isQuickSetupCollapsed && (
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="defaultWarehouse">
                    <Building className="h-4 w-4 inline mr-2" />
                    Default Warehouse
                  </Label>
                  <Select
                    value={defaultWarehouseId}
                    onValueChange={(value) => {
                      setDefaultWarehouseId(value);
                      setDefaultLocationId(""); // Reset location when warehouse changes
                    }}
                  >
                    <SelectTrigger id="defaultWarehouse">
                      <SelectValue placeholder="Select warehouse..." />
                    </SelectTrigger>
                    <SelectContent>
                      {mockWarehouses.filter(w => w.active).map(wh => (
                        <SelectItem key={wh.code} value={wh.code}>
                          {wh.name} ({wh.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="defaultLocation">
                    <MapPin className="h-4 w-4 inline mr-2" />
                    Default Location
                  </Label>
                  <Select
                    value={defaultLocationId}
                    onValueChange={setDefaultLocationId}
                    disabled={!defaultWarehouseId}
                  >
                    <SelectTrigger id="defaultLocation">
                      <SelectValue placeholder="Select location..." />
                    </SelectTrigger>
                    <SelectContent>
                      {defaultWarehouseId && getLocationsForWarehouse(defaultWarehouseId).map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={handleApplyDefaults}
                    disabled={!defaultWarehouseId || summary.itemCount === 0}
                    className="w-full"
                    variant="secondary"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Apply to Selected Items
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                This will apply the warehouse{defaultLocationId ? " and location" : ""} to all currently selected items. You can still modify individual items afterwards.
              </p>
              </CardContent>
            )}
            </Card>

            {/* Line Items */}
            <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    Items to Receive
                  </CardTitle>
                  <CardDescription>
                    Select items and enter quantities received
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={viewMode === "detailed" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("detailed")}
                  >
                    <LayoutGrid className="h-4 w-4 mr-1" />
                    Detailed
                  </Button>
                  <Button
                    variant={viewMode === "table" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("table")}
                  >
                    <LayoutList className="h-4 w-4 mr-1" />
                    Table
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search and Filters */}
              <div className="space-y-4 mb-6">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by code, name, or description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="outstandingOnly"
                      checked={showOutstandingOnly}
                      onCheckedChange={(checked) => setShowOutstandingOnly(checked as boolean)}
                    />
                    <Label htmlFor="outstandingOnly" className="cursor-pointer">
                      Outstanding only
                    </Label>
                  </div>
                </div>

                {/* Bulk Actions Toolbar */}
                <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/50 rounded-lg border">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm font-medium">Bulk Actions:</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllVisible}
                    >
                      <CheckSquare className="h-3 w-3 mr-1" />
                      Select Page ({paginatedLines.filter(l => !l.isFullyReceived).length})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllFiltered}
                    >
                      <CheckSquare className="h-3 w-3 mr-1" />
                      Select All ({filteredLines.filter(l => !l.isFullyReceived).length})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeselectAll}
                    >
                      <Square className="h-3 w-3 mr-1" />
                      Deselect All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleFillQuantitiesToMax}
                      disabled={summary.itemCount === 0}
                    >
                      <Zap className="h-3 w-3 mr-1" />
                      Fill Quantities
                    </Button>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {summary.itemCount} item{summary.itemCount !== 1 ? "s" : ""} selected
                  </div>
                </div>

                {/* Results Info */}
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div>
                    Showing {paginatedLines.length} of {filteredLines.length} items
                    {filteredLines.length !== receivingLines.length && (
                      <span> (filtered from {receivingLines.length} total)</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Line Items - Detailed View */}
              {viewMode === "detailed" && (
                <div className="space-y-4">
                  {paginatedLines.map((line) => (
                  <div
                    key={line.poLineId}
                    className={`border rounded-lg p-4 ${
                      line.isFullyReceived
                        ? "bg-muted/30 opacity-60"
                        : line.isSelected
                        ? "border-blue-200 bg-blue-50/30"
                        : ""
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <div className="pt-1">
                        <Checkbox
                          checked={line.isSelected}
                          onCheckedChange={() => handleLineToggle(line.poLineId)}
                          disabled={line.isFullyReceived}
                        />
                      </div>

                      {/* Line Details */}
                      <div className="flex-1 space-y-3">
                        {/* Header */}
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-lg">{line.stockItemName}</span>
                              <Badge variant="outline" className="font-mono">
                                {line.stockItemCode}
                              </Badge>
                              {line.isFullyReceived && (
                                <Badge variant="success" accent="success">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Fully Received
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {line.description}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">Line {line.lineNumber}</div>
                            <div className="font-medium">
                              {formatCurrency(line.unitPrice, selectedPO.currency)} / unit
                            </div>
                          </div>
                        </div>

                        {/* Quantities */}
                        <div className="grid grid-cols-3 gap-4 bg-muted/50 p-3 rounded">
                          <div>
                            <Label className="text-xs text-muted-foreground">Ordered</Label>
                            <p className="font-mono font-bold text-lg">{line.quantityOrdered}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Previously Received</Label>
                            <p className="font-mono font-bold text-lg text-green-600">
                              {line.quantityPreviouslyReceived}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Outstanding</Label>
                            <p className="font-mono font-bold text-lg text-blue-600">
                              {line.quantityOutstanding}
                            </p>
                          </div>
                        </div>

                        {/* Receiving Inputs */}
                        {!line.isFullyReceived && line.isSelected && (
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
                            <div>
                              <Label htmlFor={`qty-${line.poLineId}`}>
                                Receive Quantity *
                              </Label>
                              <Input
                                id={`qty-${line.poLineId}`}
                                type="number"
                                min="0"
                                max={line.quantityOutstanding}
                                value={line.quantityToReceive}
                                onChange={(e) =>
                                  handleQuantityChange(line.poLineId, e.target.value)
                                }
                                className="font-mono font-bold"
                              />
                              {line.quantityToReceive > line.quantityOutstanding && (
                                <p className="text-xs text-destructive mt-1">
                                  Cannot exceed outstanding
                                </p>
                              )}
                            </div>

                            <div>
                              <Label htmlFor={`wh-${line.poLineId}`}>
                                <Building className="h-3 w-3 inline mr-1" />
                                Warehouse *
                              </Label>
                              <Select
                                value={line.warehouseId}
                                onValueChange={(value) =>
                                  handleWarehouseChange(line.poLineId, value)
                                }
                              >
                                <SelectTrigger id={`wh-${line.poLineId}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {mockWarehouses.filter(w => w.active).map(wh => (
                                    <SelectItem key={wh.code} value={wh.code}>
                                      {wh.name} ({wh.code})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label htmlFor={`loc-${line.poLineId}`}>
                                <MapPin className="h-3 w-3 inline mr-1" />
                                Location *
                              </Label>
                              <Select
                                value={line.locationId}
                                onValueChange={(value) =>
                                  handleLocationChange(line.poLineId, value)
                                }
                                disabled={!line.warehouseId}
                              >
                                <SelectTrigger id={`loc-${line.poLineId}`}>
                                  <SelectValue placeholder="Select location..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {getLocationsForWarehouse(line.warehouseId).map(loc => (
                                    <SelectItem key={loc.id} value={loc.id}>
                                      {loc.name} ({loc.code})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label htmlFor={`lot-${line.poLineId}`}>
                                Lot/Batch/Serial
                              </Label>
                              <Input
                                id={`lot-${line.poLineId}`}
                                value={line.lotBatchSerial}
                                onChange={(e) =>
                                  handleLotBatchChange(line.poLineId, e.target.value)
                                }
                                placeholder="Optional"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}

              {/* Line Items - Table View */}
              {viewMode === "table" && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={paginatedLines.filter(l => !l.isFullyReceived).every(l => l.isSelected)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleSelectAllVisible();
                              } else {
                                const visibleIds = new Set(paginatedLines.map(l => l.poLineId));
                                setReceivingLines(lines =>
                                  lines.map(line =>
                                    visibleIds.has(line.poLineId) ? { ...line, isSelected: false } : line
                                  )
                                );
                              }
                            }}
                          />
                        </TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead className="text-right">Receive Qty</TableHead>
                        <TableHead>Warehouse</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Lot/Batch</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedLines.map((line) => (
                        <TableRow
                          key={line.poLineId}
                          className={line.isFullyReceived ? "opacity-50" : line.isSelected ? "bg-blue-50/50" : ""}
                        >
                          <TableCell>
                            <Checkbox
                              checked={line.isSelected}
                              onCheckedChange={() => handleLineToggle(line.poLineId)}
                              disabled={line.isFullyReceived}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">{line.stockItemCode}</TableCell>
                          <TableCell>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{line.stockItemName}</span>
                              </div>
                              <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {line.description}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold text-blue-600">
                            {line.quantityOutstanding}
                          </TableCell>
                          <TableCell>
                            {line.isSelected && !line.isFullyReceived ? (
                              <Input
                                type="number"
                                min="0"
                                max={line.quantityOutstanding}
                                value={line.quantityToReceive}
                                onChange={(e) => handleQuantityChange(line.poLineId, e.target.value)}
                                className="w-24 font-mono font-bold"
                              />
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {line.isSelected && !line.isFullyReceived ? (
                              <Select
                                value={line.warehouseId}
                                onValueChange={(value) => handleWarehouseChange(line.poLineId, value)}
                              >
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {mockWarehouses.filter(w => w.active).map(wh => (
                                    <SelectItem key={wh.code} value={wh.code}>
                                      {wh.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-muted-foreground text-sm">{line.warehouseId || "-"}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {line.isSelected && !line.isFullyReceived ? (
                              <Select
                                value={line.locationId}
                                onValueChange={(value) => handleLocationChange(line.poLineId, value)}
                                disabled={!line.warehouseId}
                              >
                                <SelectTrigger className="w-[150px]">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {getLocationsForWarehouse(line.warehouseId).map(loc => (
                                    <SelectItem key={loc.id} value={loc.id}>
                                      {loc.code}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-muted-foreground text-sm">{line.locationId || "-"}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {line.isSelected && !line.isFullyReceived ? (
                              <Input
                                value={line.lotBatchSerial}
                                onChange={(e) => handleLotBatchChange(line.poLineId, e.target.value)}
                                placeholder="Optional"
                                className="w-32"
                              />
                            ) : (
                              <span className="text-muted-foreground text-sm">{line.lotBatchSerial || "-"}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {formatCurrency(line.unitPrice, selectedPO.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}


            </CardContent>
            </Card>

            {/* Cost Impact Warning */}
            {receivingLines.some(l => l.isSelected && !l.isFullyReceived) && (
              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setIsCostImpactCollapsed(!isCostImpactCollapsed)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-amber-600" />
                        Cost Impact Analysis
                        {isCostImpactCollapsed && (
                          <Badge variant="outline" className="ml-2">
                            <Info className="h-3 w-3 mr-1" />
                            View Details
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Review how this receipt will affect stock costs
                      </CardDescription>
                    </div>
                    <div className="ml-2">
                      {isCostImpactCollapsed ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronUp className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                </CardHeader>
                {!isCostImpactCollapsed && (
                  <CardContent>
                    <CostImpactWarning
                      movementType="Purchase"
                      items={receivingLines
                        .filter(l => l.isSelected && !l.isFullyReceived)
                        .map(l => ({
                          stockCode: l.stockItemCode,
                          description: l.stockItemName,
                          quantity: l.quantityToReceive,
                          unitPrice: l.unitPrice,
                          currentAvgCost: l.unitPrice * 0.9, // Mock - in real app, fetch from API
                          currentQty: 100, // Mock - in real app, fetch from API
                        }))}
                    />
                  </CardContent>
                )}
              </Card>
            )}
          </>
        )}

        {/* Empty State */}
        {!selectedPO && !isPreloaded && (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a purchase order to begin receiving goods</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sticky Action Bar */}
      {selectedPO && receivingLines.length > 0 && (
        <div className="flex-shrink-0 border-t bg-background shadow-lg">
          <div className="p-4">
            {/* Summary Info */}
            {summary.itemCount > 0 && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-blue-700 font-medium">PO:</span>
                      <span className="ml-1 font-mono font-bold text-blue-900">{selectedPO.poNumber}</span>
                    </div>
                    <div className="h-4 w-px bg-blue-300" />
                    <div>
                      <span className="text-blue-700 font-medium">{summary.itemCount} item{summary.itemCount !== 1 ? "s" : ""}</span>
                      <span className="mx-1 text-blue-500">•</span>
                      <span className="text-blue-700 font-medium">{summary.totalUnits} units</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-blue-700">Total Value</div>
                    <div className="text-lg font-bold text-blue-900">
                      {formatCurrency(summary.totalValue, selectedPO.currency)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  if (returnTo === "purchase-orders") {
                    navigateTo("purchase-orders");
                  } else {
                    setSelectedPO(null);
                    setReceivingLines([]);
                  }
                }}
              >
                Cancel
              </Button>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleSaveDraft}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft
                </Button>
                <Button
                  onClick={handleConfirmReceipt}
                  disabled={summary.itemCount === 0}
                  size="lg"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Receipt
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Goods Receipt</DialogTitle>
            <DialogDescription>
              Please review the details before confirming
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">PO Number:</span>
                <span className="font-mono font-bold">{selectedPO?.poNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">GRN Reference:</span>
                <span className="font-mono font-bold">{grnReference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Items:</span>
                <span className="font-bold">{summary.itemCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Units:</span>
                <span className="font-bold">{summary.totalUnits}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Value:</span>
                <span className="font-bold text-lg">
                  {formatCurrency(summary.totalValue, selectedPO?.currency)}
                </span>
              </div>
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>This action cannot be undone</AlertTitle>
              <AlertDescription>
                Stock levels will be updated immediately. Make sure all information is correct.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Receipt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
